import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Plus, ArrowRight, Zap } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FirmLogo } from "@/components/firm-logo";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import { listPairs, type PairListItem } from "@/lib/api/pairs";
import { isFuturesPair, type TerminalMarket } from "@/lib/market";

/**
 * Right-side pair picker for the Terminal page. One row per
 * **account-pair**, not per symbol-pair: an account-pair that has
 * traded EURUSD + XAUUSD + GBPUSD shows once, with totals summed
 * across symbols. Clicking a row navigates to that account-pair's
 * primary (most-active) symbol-pair so /terminal/:slug routing stays
 * symbol-pair-keyed under the hood.
 *
 * Parent owns the loaded pair; the rail receives `selectedAccountPairSlug`
 * to highlight the matching group.
 */

interface PairRailProps {
  pairs: PairListItem[] | null;
  loading: boolean;
  selectedAccountPairSlug: string | null;
  onSelect(slug: string): void;
  onQuickTrade?(): void;
  market?: TerminalMarket;
}

/**
 * Aggregated view-model for one account-pair. Built from the flat
 * `PairListItem[]` returned by /api/pairs.
 */
interface AccountPairGroup {
  /** Stable key — falls back to the representative symbol-pair's slug
   *  for legacy rows where accountPairSlug is null. */
  groupKey: string;
  /** Used by the parent to detect "this is the loaded pair". Null for
   *  legacy rows. */
  accountPairSlug: string | null;
  /** Slug we navigate to on click. Picked from the active symbol-pair
   *  with the largest |lots| → first active → first overall. */
  primarySymbolPairSlug: string;
  /** Coalesced status across the group. */
  status: "active" | "setup" | "closed";
  /** Sum of netPnl across the group. */
  totalNetPnl: number;
  /** Distinct symbols traded under this account-pair. */
  symbols: string[];
  /** Used for firm logos + fallback when symbolCount === 1. */
  representative: PairListItem;
}

function groupPairsByAccountPair(pairs: PairListItem[]): AccountPairGroup[] {
  const buckets = new Map<string, PairListItem[]>();
  for (const p of pairs) {
    const key = p.accountPairSlug ?? `__legacy__:${p.slug}`;
    const list = buckets.get(key);
    if (list) list.push(p);
    else buckets.set(key, [p]);
  }

  const groups: AccountPairGroup[] = [];
  for (const [key, items] of buckets) {
    // Pick a representative: the one with the largest |lots| among
    // active rows, falling back to the first active, falling back to
    // the first item.
    const active = items.filter((i) => i.status === "active");
    const ranked = active.length > 0 ? active : items;
    const rep =
      ranked.slice().sort((a, b) => Math.abs(b.lots) - Math.abs(a.lots))[0] ??
      items[0]!;

    // Coalesced status: active beats setup beats closed.
    let status: "active" | "setup" | "closed" = "closed";
    if (items.some((i) => i.status === "active")) status = "active";
    else if (items.some((i) => i.status === "setup")) status = "setup";

    const totalNetPnl = items.reduce((sum, i) => sum + i.netPnl, 0);
    const symbols = Array.from(
      new Set(items.map((i) => i.symbol.toUpperCase())),
    );

    groups.push({
      groupKey: key,
      accountPairSlug: rep.accountPairSlug,
      primarySymbolPairSlug: rep.slug,
      status,
      totalNetPnl,
      symbols,
      representative: rep,
    });
  }

  // Sort: active first, then setup, then closed; within the same
  // status, larger absolute P&L first so the operator's eye lands on
  // the loud rows.
  const statusRank = { active: 0, setup: 1, closed: 2 } as const;
  groups.sort((a, b) => {
    const r = statusRank[a.status] - statusRank[b.status];
    if (r !== 0) return r;
    return Math.abs(b.totalNetPnl) - Math.abs(a.totalNetPnl);
  });

  return groups;
}

export function PairRail({
  pairs,
  loading,
  selectedAccountPairSlug,
  onSelect,
  onQuickTrade,
  market = "fx-cfd",
}: PairRailProps): ReactNode {
  const label = market === "futures" ? "Partner Set" : "Pair";
  const groups = useMemo(
    () => (pairs ? groupPairsByAccountPair(pairs) : []),
    [pairs],
  );
  return (
    <div className="flex h-full flex-col gap-3 rounded-xl bg-card p-3 ring-1 ring-foreground/10">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className="font-sans text-sm font-semibold text-foreground">
          Choose a <span className="text-accent">{label}</span>
        </h2>
        {pairs && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {groups.length} {groups.length === 1 ? label : `${label}s`}
          </span>
        )}
      </div>

      {/* List */}
      <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto pr-0.5">
        <AnimatePresence mode="popLayout" initial={false}>
          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-busy="true"
              aria-label="Loading pairs"
              className="flex flex-col gap-1.5"
            >
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex flex-col gap-1.5 rounded-lg border border-border bg-background/40 p-2.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-3 w-3" />
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-4 w-12 rounded-md" />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {!loading && pairs && groups.length === 0 && (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-2 px-3 py-6 text-center"
            >
              <p className="text-xs text-muted-foreground">
                {market === "futures"
                  ? "No futures partner sets yet. Pair two futures accounts to track the hedge relationship."
                  : "No pairs yet. Create your first hedge pair to start trading."}
              </p>
            </motion.div>
          )}

          {!loading &&
            groups.map((g) => {
              const selected =
                g.accountPairSlug != null &&
                g.accountPairSlug === selectedAccountPairSlug;
              return (
                <AccountPairRow
                  key={g.groupKey}
                  group={g}
                  selected={selected}
                  onSelect={() => onSelect(g.primarySymbolPairSlug)}
                />
              );
            })}
        </AnimatePresence>
      </div>

      {/* Footer CTAs */}
      <div className="flex flex-col gap-2 border-t border-border pt-3">
        <Button
          type="button"
          size="sm"
          className="w-full justify-center bg-accent text-white hover:bg-accent/90"
          render={<Link to="/pair-approvals" />}
        >
          <Plus aria-hidden="true" className="size-3.5" />
          {market === "futures" ? "New Partner Set" : "New Pair"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!selectedAccountPairSlug || !onQuickTrade}
          onClick={onQuickTrade}
          className="w-full justify-center"
          title={
            !selectedAccountPairSlug
              ? `Select a ${label.toLowerCase()} first`
              : market === "futures"
                ? "Jump to futures partner management"
                : "Jump to the order panel"
          }
        >
          <Zap aria-hidden="true" className="size-3.5" />
          {market === "futures" ? "Manage Set" : "Quick Trade"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Single row in the rail — one per account-pair. No symbol headline:
 * the operator picks a pair, then chooses the symbol from the
 * watchlist or the order panel.
 */
function AccountPairRow({
  group,
  selected,
  onSelect,
}: {
  group: AccountPairGroup;
  selected: boolean;
  onSelect(): void;
}): ReactNode {
  const statusVariant: "active" | "paused" | "breached" =
    group.status === "active"
      ? "active"
      : group.status === "setup"
        ? "paused"
        : "breached";
  const statusLabel =
    group.status.charAt(0).toUpperCase() + group.status.slice(1);
  // Slot labels are positional, NOT directional — direction is decided
  // per-trade. The two slots are just "the two accounts in this pair",
  // and the same naming should apply across futures and FX/CFD.
  const firstTitle = "Account A";
  const secondTitle = "Account B";
  const symbolChip =
    group.symbols.length === 1
      ? group.symbols[0]
      : `${group.symbols.length} symbols`;

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.15 }}
      className={cn(
        "group flex flex-col gap-1.5 rounded-lg border bg-background/40 p-2.5 text-left transition-colors",
        selected
          ? "border-accent/50 bg-accent/5 ring-1 ring-accent/40"
          : "border-border hover:border-accent/30 hover:bg-accent/[0.03]",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <FirmLogo
            src={group.representative.longAccount.firmLogo}
            name={group.representative.longAccount.firmName}
            title={firstTitle}
            className="h-4 w-4 shrink-0"
          />
          <ArrowRight aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
          <FirmLogo
            src={group.representative.shortAccount.firmLogo}
            name={group.representative.shortAccount.firmName}
            title={secondTitle}
            className="h-4 w-4 shrink-0"
          />
          <span className="truncate font-mono text-[11px] text-muted-foreground">
            {group.representative.longAccount.firmName} ↔{" "}
            {group.representative.shortAccount.firmName}
          </span>
        </div>
        <Badge variant={statusVariant} size="xs">
          {statusLabel}
        </Badge>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] text-muted-foreground">
          {symbolChip}
        </span>
        <span
          className={cn(
            "font-mono text-[10px] tabular-nums",
            group.totalNetPnl > 0
              ? "text-emerald-500"
              : group.totalNetPnl < 0
                ? "text-destructive"
                : "text-muted-foreground",
          )}
        >
          {group.totalNetPnl >= 0 ? "+" : ""}
          {fmtMoney(group.totalNetPnl)}
        </span>
      </div>
    </motion.button>
  );
}

/**
 * Hook: fetches the pairs list and refetches when the window regains focus.
 * Returns `loading` only on the first load — subsequent refetches happen
 * silently so the rail doesn't flash a spinner every time the operator
 * tabs back to the dashboard.
 */
export function usePairsList(market: TerminalMarket = "fx-cfd"): {
  pairs: PairListItem[] | null;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [pairs, setPairs] = useState<PairListItem[] | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const next = await listPairs();
      setPairs(next.filter((pair) => isFuturesPair(pair) === (market === "futures")));
    } catch (err) {
      console.error("[pair-rail] listPairs failed", err);
      setPairs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [market]);

  return { pairs, loading, refresh };
}
